/**
 * Common DealsRail component used by other modules as a reusable component which returns the ui for a product carousel.
 *
 * @module views/Organisms/DealsRail
 * @memberof -Common
 */
import './DealsRail.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import { hasItems } from '@ulta/core/utils/array/array';

import MediumDealCard from '@ulta/modules/MediumDealCard/MediumDealCard';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';
import interpolateString from '@ulta/utils/interpolateString/interpolateString';

/**
 * Represents a DealsRail component
 *
 * @method
 * @param {DealsRailProps} props - React properties passed from composition
 * @returns DealsRail
 */
const DealsRail = ( props ) => {
  const dealsRailRef = useRef( null );

  const {
    /* Cards */
    dataCapture,
    invokeMutation,
    offers = defaultProps.offers,

    /* Rail Title */
    headerTitle,
    subTitle,
    viewAllOffersAction,

    /* Rail meta data */
    countLabel,
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    nextAccessibility = defaultProps.nextAccessibility,
    nextClickAction,
    previousAccessibility = defaultProps.previousAccessibility,
    previousClickAction,
    title = defaultProps.title
  } = props;

  const hasOffers = ( offers !== null && offers !== undefined );

  const validOffers = hasOffers ? offers.filter( ( offer ) => offer?.actionGroup && offer?.eyebrow && offer?.headline && offer?.image?.imageUrl ) : [];

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  /* Interpolating tokenized strings */
  const newSubtitle = interpolateString( subTitle, [validOffers.length] );
  const newCarouselAccessibility = carouselUtils.handleTokenizedString( carouselAccessibility, [title] );
  const newCarouselCardAccessibility = carouselUtils.handleTokenizedString( carouselCardAccessibility, [title] );
  const newCarouselDetailsAccessibility = carouselUtils.handleTokenizedString( carouselDetailsAccessibility, [title] );

  /* ADA Objects */
  const railADAmeta = {
    carouselAccessibility: newCarouselAccessibility,
    carouselCardAccessibility: newCarouselCardAccessibility,
    carouselDetailsAccessibility: newCarouselDetailsAccessibility
  };

  const railMeta = {
    countLabel: countLabel,
    nextAccessibility: nextAccessibility,
    previousAccessibility: previousAccessibility
  };

  if( !hasItems( validOffers ) ){
    return null;
  }

  return (
    <div
      className='DealsRail'
      ref={ dealsRailRef }
    >
      <GridContainer>
        <Rail
          railRef={ dealsRailRef }
          id={ railUuid }
          headline={ headerTitle?.text }
          headlineTag={ headerTitle?.htmlTag || 'h2' }
          subheadline={ newSubtitle }
          viewAllAction={ viewAllOffersAction }
          items={ validOffers }
          dataCapture={ dataCapture }
          _railADAmeta={ railADAmeta }
          _railMeta={ railMeta }
          nextClickAction={ nextClickAction }
          previousClickAction={ previousClickAction }
          sliderPerViewXL={ 4 }
          slidesPerGroupXL={ 4 }
          sliderPerViewLG={ 4 }
          slidesPerGroupLG={ 4 }
          sliderPerViewMD={ 3 }
          slidesPerGroupMD={ 3 }
          sliderPerViewSM={ 1.5 }
          slidesPerGroupSM={ 1 }
        >
          { validOffers?.length > 0 && validOffers?.map( ( offer, index ) => (
            <Slide key={ index }>
              <div
                key={ `DealsCard_${index}` }
                className='DealsRail__dealCard'
              >
                <MediumDealCard
                  action1={ offer.actionGroup.action1 }
                  action2={ offer.actionGroup.action2 }
                  action1Style={ offer.actionGroup.action1Style }
                  action2Style={ offer.actionGroup.action2Style }
                  eyebrow={ offer.eyebrow }
                  headline={ offer.headline }
                  image={ offer.image }
                  isOfferActivated={ offer.isOfferActivated }
                  tag={ offer.tag }
                  urgencyTag={ offer.urgencyTag }
                  subHeadline={ offer.description }
                  activateAction={ offer.activateAction }
                  invokeMutation={ invokeMutation }
                  offerId={ offer.offerId }
                />
              </div>
            </Slide>
          ) ) }
        </Rail>
      </GridContainer>
    </div>
  );
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} countLabel='' - The default value of the count label is empty string.
 * @property {string} nextAccessibility='' - The default value of the nextAccessibility is empty string.
 * @property {array} offers=[] - The default value for offers is set to empty array.
 * @property {string} previousAccessibility='' - The default value of the previousAccessibility is empty string.
 * @property {string} title='' - The default value of the title is empty string.
 */
export const defaultProps = {
  countLabel: '',
  nextAccessibility: '',
  offers: [],
  previousAccessibility: '',
  title: ''
};

/**
* Property type definitions
* @typedef DealsRailProps
* @type {object}
* @property {string} carouselAccessibility - Value for aria label for product rail
* @property {string} carouselCardAccessibility - Value for the product card aria label
* @property {string} carouselDetailsAccessibility - Value for aria label for carousel
* @property {string} countLabel - Sets the count label for productRail list.
* @property {object} headerTitle - set the offer title with proper accessiblity.
* @property {string} nextAccessibility - Sets the aria label for next button.
* @property {object} nextClickAction - The prop that holds the action for the next button.
* @property {Array.<{promoText: string, reviewCount: Number, rating: Number, brandName: string, productName: string, variantLabel: string, badge: string, image: string, availability: array, addToBagAccessibility: string, additionalOffersText: string, altImage: string, bookmarkAccessibility: string, bookmarked: boolean, kitPrice: string, listPrice: string, salePrice: string, skuId: string}>} offers - List of offers to loop over and display in carousel.
* @property {string} previousAccessibility - Sets the aria label for previous button.
* @property {object} previousClickAction - The prop that holds the action for the previous button
* @property {string} subTitle - Sets the subtitle for productRail list.
* @property {string} title - the offer title in plain text
* @property {object} viewAllOffersAction - This is the action object with action label and url
*/
export const propTypes = {
  /**
   * Value for aria label for product rail
   */
  carouselAccessibility: PropTypes.string,
  /**
   * Value for the product card aria label
   */
  carouselCardAccessibility: PropTypes.string,
  /**
   * Value for aria label for carousel
   */
  carouselDetailsAccessibility: PropTypes.string,
  /**
  * The prop that holds the count label for the product list.
  */
  countLabel: PropTypes.string,
  /**
  * The prop that holds title for the carousel.
  */
  headerTitle: PropTypes.shape( {
    htmlTag: PropTypes.string,
    text: PropTypes.string
  } ),
  /**
  * The prop that holds the aria label for next button.
  */
  nextAccessibility: PropTypes.string,
  /**
   * The prop that holds the action for the next button.
   */
  nextClickAction: PropTypes.object,
  /**
  * The prop that holds the list of offers to render.
  */
  offers: PropTypes.arrayOf(
    PropTypes.shape( {
      promoText: PropTypes.string,
      reviewCount: PropTypes.number,
      rating: PropTypes.number,
      brandName: PropTypes.string,
      productName: PropTypes.string,
      variantLabel: PropTypes.string,
      badge: PropTypes.string,
      image: PropTypes.object,
      availability: PropTypes.arrayOf( PropTypes.string ),
      listPrice: PropTypes.string,
      salePrice: PropTypes.string,
      kitPrice: PropTypes.string,
      bookmarkAccessibility: PropTypes.string,
      addToBagAccessibility: PropTypes.string,
      additionalOffersText: PropTypes.string,
      altImage: PropTypes.string,
      bookmarked: PropTypes.bool,
      skuId: PropTypes.string
    } )
  ),
  /**
   * The prop that holds the aria label for previous button.
   */
  previousAccessibility: PropTypes.string,
  /**
   * The prop that holds the action for the previous button
   */
  previousClickAction: PropTypes.object,
  /**
  * The prop that holds subtitle for the carousel.
  */
  subTitle: PropTypes.string,
  /**
  * The prop that holds the plain text title.
  */
  title: PropTypes.string,
  /**
  * This is the action with label and url.
  */
  viewAllOffersAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string

  } )
};

DealsRail.propTypes = propTypes;
DealsRail.defaultProps = defaultProps;

export default DealsRail;